import { ShoppingBag, CheckCircle } from "lucide-react";
import type { SubscriptionPackage, PackagePermissions } from "../../types/subscription";
import { gh, percentSaved } from "../../lib/packageFormatters";

interface PricingCardProps {
  pkg: SubscriptionPackage;
  isBest: boolean;
  onSelect: (pkg: SubscriptionPackage) => void;
  showSelectButton?: boolean;
}

const PricingCard = ({ pkg, isBest, onSelect, showSelectButton = true }: PricingCardProps) => {
  const details = pkg.PackageDetails?.[0] ?? ({} as PackagePermissions);
  const styles = pkg.PackageStyles?.[0] ?? {};
  const billings = pkg.PackageBilling ?? [];

  const monthlyBilling = billings.find((b) =>
    b.monthlyPeriod?.toLowerCase().includes("month")
  );
  const yearlyBilling = billings.find((b) =>
    b.monthlyPeriod?.toLowerCase().includes("year")
  );

  const monthlyAmount = monthlyBilling ? monthlyBilling.bill : null;
  const yearlyAmount = yearlyBilling ? yearlyBilling.bill : null;
  const savePct = percentSaved(monthlyAmount, yearlyAmount);

  const cardBg = styles.bgColor as string || "#090263";
  const textColor = styles.textColor ?? "#EDEFF7";

  const benefits = [
    Number(details.itemLimit) === 0
      ? "Unlimited products"
      : `Up to ${details.itemLimit} products`,
    Number(details.monthlySalesLimit) === 0
      ? "Unlimited monthly sales"
      : `Up to ${details.monthlySalesLimit} monthly sales`,
    Number(details.employeeLimit) === 0
      ? "Unlimited team members"
      : `Up to ${details.employeeLimit} team members`,
    Number(details.storeLimit) === 0
      ? "Unlimited stores"
      : `Up to ${details.storeLimit} stores`,
  ].filter(Boolean);

  const featureBullets: string[] = [];
  if (details.hasAnalytics) featureBullets.push("Advanced analytics");
  if (details.hasInventoryOverview) featureBullets.push("Inventory overview");
  if (details.hasNotifications) featureBullets.push("Smart notifications");
  if (details.hasDemandForecasting) featureBullets.push("Demand forecasting");
  if (details.hasLoyaltyProgram) featureBullets.push("Loyalty program");

  return (
    <div
      className={`relative w-full min-w-[280px] rounded-2xl shadow-xl transform transition hover:-translate-y-1 hover:shadow-2xl overflow-hidden ${isBest ? "lg:scale-105" : ""
        }`}
      style={{
        background: "white",
        color: textColor,
        border: `1px solid ${cardBg}`,
      }}
    >
      {/* Best value ribbon */}
      {isBest && (
        <div className="absolute right-1 top-2 z-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-yellow-400/95 border border-yellow-900 px-3 py-1 text-xs font-semibold text-black shadow">
            Best value
          </div>
        </div>
      )}

      {/* Header with package name */}
      <div style={{ backgroundColor: cardBg }}>
        <h3
          className="text-2xl py-5 font-semibold text-center"
          style={{
            color: textColor,
            borderBottom: `1px solid ${textColor}`,
          }}
        >
          {pkg.name}
        </h3>
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col">
        {/* Pricing blocks */}
        <div className="flex flex-col items-center gap-3 mb-4">
          {/* Monthly */}
          <div
            className="rounded-lg bg-white px-4 py-2 text-center border-1 w-full"
            style={{ color: textColor, border: `1px solid ${textColor}` }}
          >
            <div className="text-xs uppercase opacity-80">Monthly</div>
            <div className="mt-1 text-lg font-bold">{gh(monthlyAmount)}</div>
            <div className="text-xs opacity-80">
              billed {monthlyBilling?.monthlyPeriod ?? "monthly"}
            </div>
          </div>

          {/* Yearly */}
          <div
            className="rounded-lg bg-white px-4 py-2 text-center border-1 w-full"
            style={{ color: textColor, border: `1px solid ${textColor}` }}
          >
            <div className="flex items-center justify-center gap-2">
              <div className="text-xs uppercase opacity-85">Yearly</div>
              {savePct ? (
                <div className="ml-2 rounded-full bg-yellow-400 px-2 py-0.5 text-xs font-semibold text-black">
                  Save {savePct}%
                </div>
              ) : null}
            </div>
            <div className="mt-1 text-lg font-bold">{gh(yearlyAmount)}</div>
            <div className="text-xs opacity-80">
              billed {yearlyBilling?.monthlyPeriod ?? "yearly"}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div
          className="my-4 h-px w-full opacity-25"
          style={{ backgroundColor: textColor }}
        />

        {/* Benefits */}
        <div className="grid gap-2 mb-4 flex-1">
          {benefits.map((b, i) => (
            <div
              key={i}
              className="flex gap-2 text-left text-gray-800 font-semibold opacity-95"
            >
              <ShoppingBag className="w-5 h-5 text-primary flex-shrink-0" />
              <span className="text-sm">{b}</span>
            </div>
          ))}

          {/* Feature bullets */}
          {featureBullets.length > 0 && (
            <div className="flex flex-col gap-2 mt-2">
              {featureBullets.map((f, i) => (
                <div
                  key={i}
                  className="flex gap-2 p-2 justify-start items-center rounded-md"
                  style={{ border: `1px solid ${cardBg}` }}
                >
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  <span className="text-sm text-gray-800 font-semibold">{f}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA */}
        {
          showSelectButton && (
            <button
              onClick={() => onSelect(pkg)}
              className="w-full text-center gap-2 rounded-full bg-primary px-4 py-4 text-sm font-semibold text-white shadow hover:opacity-95 transition mt-4"
            >
              Select plan
            </button>
          )}
      </div>
    </div>
  );
};

export default PricingCard;

